import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const headCellSx = {
  fontWeight: 700,
  fontSize: "11px",
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
  padding: "10px 14px",
  whiteSpace: "nowrap",
};

const UserRefundsTable = ({ userRefundsData, setUserRefundsData }) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  const filteredRefunds = searchTerm
    ? userRefundsData.filter((item) =>
        item.contact?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : userRefundsData;

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        overflow: "hidden",
        mb: 3,
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          background: "#04343a",
          px: "20px",
          py: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
            Refunds Table
          </Typography>
          <Chip
            label={`Total: ${filteredRefunds.length}`}
            size="small"
            sx={{ bgcolor: "rgba(1,101,81,0.3)", color: "#fff", fontWeight: 600, fontSize: "12px" }}
          />
        </Box>

        {/* Search */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by mobile"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
                bgcolor: "rgba(255,255,255,0.1)",
                "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
              "& .MuiOutlinedInput-input": { color: "#fff" },
              "& .MuiOutlinedInput-input::placeholder": { color: "rgba(255,255,255,0.5)" },
            }}
          />
          <Button
            size="small"
            onClick={handleSearch}
            disabled={!searchInput.trim()}
            startIcon={<SearchIcon />}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "7px",
              px: 2,
              fontSize: "12px",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)" },
            }}
          >
            Search
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7", overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 900,
            "& .MuiTableCell-root": {
              padding: "9px 14px",
              fontSize: "12px",
              borderBottom: "1px solid #f0f4f8",
              whiteSpace: "nowrap",
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              background: "#f0faf5",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 45 }}>S.No.</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 170 }}>Email</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Contact</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 150 }}>Refund ID</TableCell>
              <TableCell sx={{ ...headCellSx, width: 120 }}>Refund Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: 150 }}>Refund Initiated Date</TableCell>
              <TableCell sx={{ ...headCellSx, width: 90 }}>Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: 130 }}>Created On</TableCell>
              <TableCell sx={{ ...headCellSx, width: 130 }}>Modified On</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRefunds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5, color: "#94a3b8", fontSize: "13px" }}>
                  No refund records found.
                </TableCell>
              </TableRow>
            ) : (
              filteredRefunds.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ color: "#94a3b8" }}>{index + 1}</TableCell>
                  <TableCell sx={{ color: "#1e293b" }}>{item.email}</TableCell>
                  <TableCell sx={{ color: "#64748b" }}>{item.contact}</TableCell>
                  <TableCell sx={{ color: "#64748b" }}>{item.refund_id || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.refund_status || "N/A"}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "11px",
                        bgcolor: item.refund_status === "processed" ? "#dcfce7" : "#fef9c3",
                        color: item.refund_status === "processed" ? "#15803d" : "#854d0e",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#64748b" }}>{item.refund_initiated_date || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.is_confirmed ? "Confirmed" : "Pending"}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "11px",
                        bgcolor: item.is_confirmed ? "#dcfce7" : "#fee2e2",
                        color: item.is_confirmed ? "#15803d" : "#b91c1c",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#64748b" }}>{item.created_at}</TableCell>
                  <TableCell sx={{ color: "#64748b" }}>{item.modified_at}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserRefundsTable;
